import { TriggerProps } from '@atlaskit/popup';
import { useContextualMenu } from '@databases/contextual-menu';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectValueRange,
  selectValueSingle,
} from '@databases/redux/cellSelections/actions';
import {
  selectIsValueSelected,
  selectSelectedEntireEntryIds,
} from '@databases/redux/cellSelections/selectors';
import { getDatabaseFieldRequired } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { FunctionComponent, MutableRefObject, useCallback, useState } from 'react';

import SelectValueMenu from '../database-content/contextual-menu/SelectValueMenu';
import FieldNameWithActions from '../database-field/field-name/FieldNameWithActions';
import DatabaseValueInlineEdit from '../database-value/DatabaseValueInlineEdit';

export interface DatabaseEntryRowProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  editingIds?: MutableRefObject<Set<string>>;
  autoFocus?: boolean;
}

/**
 * Renders a table that shows all the field names and values of a database entry. The field names and values can be edited
 * using inline edit fields, and new fields can be added. Any edits will cause a database update to be drafted automatically.
 */
const DatabaseEntryRow: FunctionComponent<DatabaseEntryRowProps> = ({
  contentId,
  entryId,
  fieldId,
  isContentEditable,
  canEditContent,
  editingIds,
  autoFocus,
  canEditFields,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const { view } = useView();
  const isInlineCommentsEnabled = useBooleanFeatureFlag(
    'databases-frontend-inline-comments'
  );
  const [isHover, setIsHover] = useState(false);

  const [field, isSelected] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const entriesSelected = selectSelectedEntireEntryIds(state, {
      contentId,
    });
    const isSelected = entriesSelected.length
      ? undefined
      : selectIsValueSelected(state, { entryId, fieldId });
    return [getDatabaseFieldRequired(database, fieldId), isSelected] as const;
  });

  const initialIsEditing = editingIds?.current.has(field.id);
  if (initialIsEditing) {
    // Since we use editingIds only to set the initialIsEditing prop, it is enough to set it once during the initial
    // render of the inline edit field. We don't want the field to steal the focus again when it is hidden and then
    // shown again because of a filter change. Because of this, we remove the entry from the list once it has been
    // focused for the first time.
    editingIds!.current.delete(field.id);
  }

  const { toggleOpenMenu } = useContextualMenu({
    type: 'value',
    entryId,
    fieldId,
    panel: true,
  });

  const onIsSelectedChange = useCallback(
    (options?: { isMultiSelectOn?: boolean }) => {
      if (isContentEditable) {
        if (options?.isMultiSelectOn) {
          dispatch(
            selectValueRange({
              contentId,
              entryId: entryId,
              fieldId: fieldId,
            })
          );
        } else {
          dispatch(
            selectValueSingle({
              entryId: entryId,
              fieldId: fieldId,
            })
          );
        }
      }
    },
    [isContentEditable, dispatch, contentId, entryId, fieldId]
  );

  const handleOnContextMenu = useCallback(
    (e: React.MouseEvent) => {
      onIsSelectedChange();
      if (isContentEditable) {
        e.preventDefault();
        toggleOpenMenu();
      }
    },
    [onIsSelectedChange, isContentEditable, toggleOpenMenu]
  );

  const triggerComponent = useCallback(
    (triggerProps: TriggerProps) => (
      <td
        onContextMenu={handleOnContextMenu}
        ref={triggerProps?.ref}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <DatabaseValueInlineEdit
          contentId={contentId}
          fieldId={fieldId}
          entryId={entryId}
          initialIsEditing={initialIsEditing || autoFocus}
          isReadonly={!canEditContent}
          isContentEditable={isContentEditable}
          isSelected={isSelected}
          onIsSelectedChange={onIsSelectedChange}
          enableSelection
          panel={true}
          isHover={isHover}
          {...triggerProps}
        />
      </td>
    ),
    [
      autoFocus,
      canEditContent,
      contentId,
      entryId,
      fieldId,
      handleOnContextMenu,
      initialIsEditing,
      isContentEditable,
      isHover,
      isSelected,
      onIsSelectedChange,
    ]
  );

  return (
    <tr onContextMenu={(e) => e.preventDefault()}>
      <th>
        <FieldNameWithActions
          contentId={contentId}
          fieldId={fieldId}
          isReadonly={true}
          isSelected={false}
        />
      </th>
      {isInlineCommentsEnabled ? (
        <SelectValueMenu
          scope={'main'}
          Trigger={triggerComponent}
          contentId={contentId}
          currentViewId={view.id}
          canEditContent={canEditContent}
          canEditFields={canEditFields}
          entryId={entryId}
          fieldId={fieldId}
          placement={'auto-end'}
          panel={true}
        />
      ) : (
        <td>
          <DatabaseValueInlineEdit
            contentId={contentId}
            fieldId={fieldId}
            entryId={entryId}
            initialIsEditing={initialIsEditing || autoFocus}
            isReadonly={!canEditContent}
            isContentEditable={isContentEditable}
            isSelected={isSelected}
            onIsSelectedChange={onIsSelectedChange}
            enableSelection
          />
        </td>
      )}
    </tr>
  );
};

export default DatabaseEntryRow;
